import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchManagementCompanies,
  fetchAllManagementCompanies,
} from "../../../../../redux/mgmtCo/managementSlice";
import {
  selectCurrentPage,
  selectRowsPerPage,
  setCurrentPage,
} from "../../../../../redux/mgmtCo/paginationSlice";
import {
  selectedSelectedManagement,
  setSelectedManagement,
} from "../../../../../redux/mgmtCo/selectedManagementSlice";
import useDebounce from "../../../../../hooks/useDebounce";
import useDeleteManagementCompany from "./useDeleteManagementCompany";
import useAddManagement from "./useAddManagement";
import useTableSorting from "../../../../../hooks/useTableSorting";
import {
  fetchAllContactsThunk,
  fetchAssignedContactsThunk,
} from "../../../../../redux/mgmtCo/contactsSlice";
import {
  fetchAllCustomersThunk,
  fetchAssignedCustomersThunk,
} from "../../../../../redux/mgmtCo/managementCustomerSlice";
// import { fetchAllCustomers } from "../services/managementCustomerService";
// import { fetchAllContacts } from "../services/managementContactService";
// import { fetchAssignedContacts } from "../services/managementContactService";
// import { fetchAssignedCustomers } from "../services/managementCustomerService";

const useListPage = () => {
  const dispatch = useDispatch();

  // Redux state selectors
  const companies = useSelector((state) => state.management.companies);
  const currentPage = useSelector(selectCurrentPage);
  const rowsPerPage = useSelector(selectRowsPerPage);
  const { loading } = useSelector((state) => state.management);
  const selectedManagement = useSelector(selectedSelectedManagement);

  // Custom hooks
  const { handleSubmit } = useAddManagement();
  const deleteSelected = useDeleteManagementCompany();
  const { sortField, order, handleSort } = useTableSorting(
    "managementId",
    "asc"
  );

  // Local component states
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [listExpanded, setListExpanded] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [searchQuery, setSearchQuery] = useState("");

  // Snackbar handler
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleAddSuccess = () => {
    setSnackbar({
      open: true,
      message: "Management company added successfully.",
      severity: "success",
    });
  };

  // Debounced search query to reduce frequent dispatches
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Function to update/re-fetch the companies list and related data
  const updateList = useCallback(async () => {
    try {
      if (selectedManagement && selectedManagement.managementId) {
        dispatch(fetchAssignedContactsThunk(selectedManagement.managementId));
        dispatch(fetchAssignedCustomersThunk(selectedManagement.managementId));
      }
      dispatch(fetchAllCustomersThunk());
      dispatch(fetchAllContactsThunk());
    } catch (error) {
      console.error("Error updating management companies list:", error);
    }
  }, [dispatch, selectedManagement]);

  // Initial fetch with sorting and pagination
  useEffect(() => {
    // Single unified fetch call
    dispatch(
      fetchManagementCompanies({
        currentPage,
        rowsPerPage,
        searchQuery: debouncedSearchQuery,
        sortField,
        sortOrder: order,
      })
    );
  }, [
    dispatch,
    currentPage,
    rowsPerPage,
    debouncedSearchQuery,
    sortField,
    order,
  ]);

  // Re-fetch companies when dependencies change
  useEffect(() => {
    updateList();
  }, [updateList]);

  // fetch all companies when the component mounts
  useEffect(() => {
    dispatch(fetchAllManagementCompanies());
  }, [dispatch]);

  // Add new company and open view modal
  const handleAddAndView = async () => {
    try {
      const newManagement = await handleSubmit();
      if (newManagement) {
        dispatch(setSelectedManagement(newManagement));

        // Refresh companies list with default search and sorting options
        const result = await dispatch(
          fetchManagementCompanies({
            currentPage,
            rowsPerPage,
            searchQuery: "",
            sortField,
            sortOrder: order,
          })
        );
        const newTotalPages = result.payload.totalPages;
        if (currentPage !== newTotalPages) {
          dispatch(setCurrentPage(newTotalPages));
        }
        setIsViewOpen(true);
        handleAddSuccess();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  // Delete selected management company
  const handleDeleteSelected = async () => {
    try {
      await deleteSelected();
      setSnackbar({
        open: true,
        message: "Management company deleted successfully.",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error",
      });
    }
  };

  // Close view modal and trigger add-and-view
  const handleOpenAddFromView = () => {
    setIsViewOpen(false);
    handleAddAndView();
  };

  return {
    loading,
    companies,
    currentPage,
    rowsPerPage,
    searchQuery,
    setSearchQuery,
    debouncedSearchQuery,
    sortField,
    order,
    isViewOpen,
    setIsViewOpen,
    listExpanded,
    setListExpanded,
    snackbar,
    handleSnackbarClose,
    handleAddAndView,
    handleOpenAddFromView,
    handleDeleteSelected,
    updateList,
    handleSort,
  };
};

export default useListPage;
